import { useMemo, useState, useEffect } from"react";
import { motion, AnimatePresence } from"framer-motion";
import {
 Play,
 RotateCcw,
 ChevronRight,
 ChevronLeft,
 BookOpen,
 FileText,
 BarChart3,
 LayoutGrid,
 Sparkles,
} from"lucide-react";

// 2 écritures pédagogiques qui couvrent 6 comptes répartis sur 3 classes
const ECRITURES = [
 {
 id:"ec1",
 libelle:"Vente facture n°2025-001",
 lignes: [
 { num:"411", lib:"Clients", debit: 2400, credit: 0, classe: 4, dest:"bilan-actif" },
 { num:"707", lib:"Ventes de marchandises", debit: 0, credit: 2000, classe: 7, dest:"cr-produit" },
 { num:"44571", lib:"TVA collectée", debit: 0, credit: 400, classe: 4, dest:"bilan-passif" },
 ],
 },
 {
 id:"ec2",
 libelle:"Achat fournitures n°F-1234",
 lignes: [
 { num:"606", lib:"Achats non stockés", debit: 100, credit: 0, classe: 6, dest:"cr-charge" },
 { num:"44566", lib:"TVA déductible", debit: 20, credit: 0, classe: 4, dest:"bilan-actif" },
 { num:"401", lib:"Fournisseurs", debit: 0, credit: 120, classe: 4, dest:"bilan-passif" },
 ],
 },
];

const COLOR_BY_CLASS = {
 1:"#2D6BE0", 2:"#A56A2E", 3:"#2F7A5C", 4:"#A33B4B",
 5:"#2D6BE0", 6:"#B85724", 7:"#2F7A5C", 8:"#474D59",
};

// Aggrège toutes les lignes par compte → grand livre
function buildGrandLivre(ecritures) {
 const map = new Map();
 for (const ec of ecritures) {
 for (const l of ec.lignes) {
 if (!map.has(l.num)) {
 map.set(l.num, { num: l.num, lib: l.lib, classe: l.classe, dest: l.dest, debit: 0, credit: 0 });
 }
 const acc = map.get(l.num);
 acc.debit += l.debit;
 acc.credit += l.credit;
 }
 }
 return Array.from(map.values()).sort((a, b) => a.num.localeCompare(b.num));
}

const STEPS = [
 { id: 0, key:"journal", label:"1 · Journal", icon: BookOpen },
 { id: 1, key:"grand-livre", label:"2 · Grand livre", icon: FileText },
 { id: 2, key:"balance", label:"3 · Balance", icon: BarChart3 },
 { id: 3, key:"etats", label:"4 · Bilan & Résultat", icon: LayoutGrid },
];

const fmt = (n) =>
 n === 0 ?"—" : n.toLocaleString("fr-FR", { minimumFractionDigits: 2 });

export default function FluxComptable() {
 const [step, setStep] = useState(0);
 const [auto, setAuto] = useState(false);

 const gl = useMemo(() => buildGrandLivre(ECRITURES), []);

 useEffect(() => {
 if (!auto) return;
 if (step >= STEPS.length - 1) {
 setAuto(false);
 return;
 }
 const t = setTimeout(() => setStep((s) => s + 1), 2500);
 return () => clearTimeout(t);
 }, [auto, step]);

 const reset = () => {
 setStep(0);
 setAuto(false);
 };

 const totalsByZone = useMemo(() => {
 const z = {"bilan-actif": { d: 0, c: 0, label:"Actif (bilan)" },"bilan-passif": { d: 0, c: 0, label:"Passif (bilan)" },"cr-charge": { d: 0, c: 0, label:"Charges (résultat)" },"cr-produit": { d: 0, c: 0, label:"Produits (résultat)" },
 };
 gl.forEach((a) => {
 z[a.dest].d += a.debit;
 z[a.dest].c += a.credit;
 });
 return z;
 }, [gl]);

 const resultat = totalsByZone["cr-produit"].c - totalsByZone["cr-charge"].d;

 return (
 <div data-testid="flux-view" className="space-y-6">
 {/* Hero */}
 <div className="bg-gradient-to-br from-[#15294F] to-[#1F3B6B] text-white rounded-2xl p-6 md:p-8 shadow-keyto">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
 <Sparkles className="text-[#5AA0FF]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#5AA0FF]/40 bg-[#5AA0FF]/10 text-[#5AA0FF] mb-3">
 Le déclic
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold leading-tight">
 Du journal au bilan
 </h1>
 <p className="mt-3 text-white/80 max-w-2xl">
 Comprendre <strong>comment un montant voyage</strong> entre les
 livres comptables : du journal → vers le grand livre → vers la
 balance → vers le bilan et le compte de résultat. En 4 étapes,
 animé.
 </p>
 </div>
 </div>

 {/* Stepper */}
 <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
 {STEPS.map((s, i) => {
 const Icon = s.icon;
 const isActive = step === i;
 const isDone = step > i;
 return (
 <button
 key={s.id}
 data-testid={`flux-step-${i}`}
 onClick={() => setStep(i)}
 className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left transition border ${
 isActive
 ?"bg-white text-[#15294F] border-white"
 : isDone
 ?"bg-[#5AA0FF]/20 text-white border-[#5AA0FF]/40"
 :"bg-white/5 text-white/60 border-white/10 hover:bg-white/10"
 }`}
 >
 <Icon size={16} />
 <span className="text-xs font-semibold">{s.label}</span>
 </button>
 );
 })}
 </div>

 {/* Controls */}
 <div className="mt-4 flex flex-wrap items-center gap-2">
 <button
 data-testid="flux-prev"
 onClick={() => setStep((s) => Math.max(0, s - 1))}
 disabled={step === 0}
 className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs font-semibold disabled:opacity-30"
 >
 <ChevronLeft size={14} /> Précédent
 </button>
 <button
 data-testid="flux-next"
 onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
 disabled={step >= STEPS.length - 1}
 className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#5AA0FF] hover:bg-[#7BB8FF] text-[#15294F] text-xs font-bold disabled:opacity-30"
 >
 Suivant <ChevronRight size={14} />
 </button>
 <button
 data-testid="flux-autoplay"
 onClick={() => {
 if (step >= STEPS.length - 1) setStep(0);
 setAuto((v) => !v);
 }}
 className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs font-semibold"
 >
 <Play size={14} /> {auto ?"Pause" :"Lecture auto"}
 </button>
 <button
 data-testid="flux-reset"
 onClick={reset}
 className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-transparent hover:bg-white/10 text-xs font-semibold text-white/70"
 >
 <RotateCcw size={14} /> Recommencer
 </button>
 </div>
 </div>

 {/* Scène */}
 <div className="bg-white border border-keyto rounded-2xl shadow-keyto-sm overflow-hidden">
 <AnimatePresence mode="wait">
 {step === 0 && <JournalScene key="journal" />}
 {step === 1 && <GrandLivreScene key="gl" gl={gl} />}
 {step === 2 && <BalanceScene key="balance" gl={gl} />}
 {step === 3 && (
 <EtatsScene
 key="etats"
 gl={gl}
 totalsByZone={totalsByZone}
 resultat={resultat}
 />
 )}
 </AnimatePresence>
 </div>

 {/* Explication contextuelle */}
 <ExplainBox step={step} />
 </div>
 );
}

// ─── Scène 1 : Journal ──────────────────────────────────────────────────
function JournalScene() {
 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.4 }}
 className="p-6 md:p-8"
 data-testid="flux-scene-journal"
 >
 <SectionTitle icon={BookOpen} title="Le journal" subtitle="Toutes les opérations, dans l'ordre chronologique." />
 <div className="mt-6 space-y-6">
 {ECRITURES.map((ec, idx) => (
 <motion.div
 key={ec.id}
 initial={{ opacity: 0, y: 12 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 + idx * 0.4, duration: 0.4 }}
 className="rounded-xl border border-keyto overflow-hidden"
 >
 <div className="bg-slate-50 px-4 py-2 flex items-center justify-between border-b border-keyto">
 <span className="text-xs font-mono text-slate-500">15/01/2025</span>
 <span className="text-sm font-semibold text-[#15294F]">{ec.libelle}</span>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="text-xs text-slate-500 uppercase border-b border-keyto">
 <th className="px-4 py-2 text-left">N°</th>
 <th className="px-4 py-2 text-left">Libellé</th>
 <th className="px-4 py-2 text-right">Débit</th>
 <th className="px-4 py-2 text-right">Crédit</th>
 </tr>
 </thead>
 <tbody>
 {ec.lignes.map((l, i) => {
 const c = COLOR_BY_CLASS[l.classe];
 return (
 <motion.tr
 key={i}
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.4 + idx * 0.4 + i * 0.15, duration: 0.3 }}
 className="border-t border-slate-100"
 >
 <td className="px-4 py-2 font-mono font-bold" style={{ color: c }}>{l.num}</td>
 <td className="px-4 py-2 text-slate-700">{l.lib}</td>
 <td className="px-4 py-2 text-right font-mono">{fmt(l.debit)}</td>
 <td className="px-4 py-2 text-right font-mono">{fmt(l.credit)}</td>
 </motion.tr>
 );
 })}
 </tbody>
 </table>
 </motion.div>
 ))}
 </div>
 </motion.div>
 );
}

// ─── Scène 2 : Grand livre (comptes en T) ──────────────────────────────
function GrandLivreScene({ gl }) {
 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.4 }}
 className="p-6 md:p-8"
 data-testid="flux-scene-grandlivre"
 >
 <SectionTitle
 icon={FileText}
 title="Le grand livre"
 subtitle="Mêmes lignes, mais regroupées par compte (un compte = un « T »)."
 />
 <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
 {gl.map((a, idx) => {
 const c = COLOR_BY_CLASS[a.classe];
 return (
 <motion.div
 key={a.num}
 layoutId={`acc-${a.num}`}
 initial={{ opacity: 0, scale: 0.85 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: idx * 0.1, duration: 0.35 }}
 className="rounded-xl border border-keyto overflow-hidden bg-white"
 style={{ borderTop: `4px solid ${c}` }}
 >
 <div className="px-3 py-2 bg-slate-50 border-b border-keyto">
 <p className="text-[10px] font-bold tracking-wide" style={{ color: c }}>
 COMPTE {a.num}
 </p>
 <p className="text-xs text-slate-600 leading-tight">{a.lib}</p>
 </div>
 <div className="grid grid-cols-2 text-xs">
 <div className="px-3 py-3 border-r border-keyto bg-blue-50/40">
 <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Débit</p>
 <p className="font-mono font-bold text-[#15294F]">{fmt(a.debit)}</p>
 </div>
 <div className="px-3 py-3 bg-rose-50/30">
 <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Crédit</p>
 <p className="font-mono font-bold text-[#15294F]">{fmt(a.credit)}</p>
 </div>
 </div>
 <div className="px-3 py-2 bg-slate-50 border-t border-keyto text-[11px] flex justify-between">
 <span className="text-slate-500">Solde</span>
 <span className="font-mono font-bold" style={{ color: c }}>
 {fmt(Math.abs(a.debit - a.credit))} {a.debit > a.credit ?"D" :"C"}
 </span>
 </div>
 </motion.div>
 );
 })}
 </div>
 </motion.div>
 );
}

// ─── Scène 3 : Balance ──────────────────────────────────────────────────
function BalanceScene({ gl }) {
 const totDeb = gl.reduce((a, l) => a + l.debit, 0);
 const totCre = gl.reduce((a, l) => a + l.credit, 0);
 const sumDebSold = gl.reduce((a, l) => a + Math.max(0, l.debit - l.credit), 0);
 const sumCreSold = gl.reduce((a, l) => a + Math.max(0, l.credit - l.debit), 0);

 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.4 }}
 className="p-6 md:p-8"
 data-testid="flux-scene-balance"
 >
 <SectionTitle
 icon={BarChart3}
 title="La balance"
 subtitle="Reprise du grand livre : tous les comptes, leurs totaux et leurs soldes. Doit s'équilibrer."
 />
 <div className="mt-6 overflow-x-auto rounded-xl border border-keyto">
 <table className="w-full text-sm">
 <thead className="bg-slate-100">
 <tr className="text-xs text-[#15294F] uppercase">
 <th className="px-3 py-2 text-left">N°</th>
 <th className="px-3 py-2 text-left">Libellé</th>
 <th className="px-3 py-2 text-right">Mvt. Débit</th>
 <th className="px-3 py-2 text-right">Mvt. Crédit</th>
 <th className="px-3 py-2 text-right">Solde D</th>
 <th className="px-3 py-2 text-right">Solde C</th>
 </tr>
 </thead>
 <tbody>
 {gl.map((a, i) => {
 const c = COLOR_BY_CLASS[a.classe];
 const sd = Math.max(0, a.debit - a.credit);
 const sc = Math.max(0, a.credit - a.debit);
 return (
 <motion.tr
 key={a.num}
 initial={{ opacity: 0, y: 8 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: i * 0.08 }}
 className="border-t border-slate-100"
 >
 <td className="px-3 py-2 font-mono font-bold" style={{ color: c }}>{a.num}</td>
 <td className="px-3 py-2 text-slate-700">{a.lib}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(a.debit)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(a.credit)}</td>
 <td className="px-3 py-2 text-right font-mono text-[#15294F]">{fmt(sd)}</td>
 <td className="px-3 py-2 text-right font-mono text-[#15294F]">{fmt(sc)}</td>
 </motion.tr>
 );
 })}
 <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-[#15294F]">
 <td colSpan={2} className="px-3 py-2 text-right">Totaux</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(totDeb)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(totCre)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(sumDebSold)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(sumCreSold)}</td>
 </tr>
 </tbody>
 </table>
 </div>
 <p className="mt-3 text-xs text-slate-500">
 ✓ Total débits = Total crédits ({fmt(totDeb)}) — équilibre vérifié. La
 partie double est respectée.
 </p>
 </motion.div>
 );
}

// ─── Scène 4 : Bilan + Compte de résultat ──────────────────────────────
function EtatsScene({ gl, totalsByZone, resultat }) {
 const bilanActif = gl.filter((a) => a.dest ==="bilan-actif");
 const bilanPassif = gl.filter((a) => a.dest ==="bilan-passif");
 const crCharges = gl.filter((a) => a.dest ==="cr-charge");
 const crProduits = gl.filter((a) => a.dest ==="cr-produit");

 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.4 }}
 className="p-6 md:p-8"
 data-testid="flux-scene-etats"
 >
 <SectionTitle
 icon={LayoutGrid}
 title="Le bilan & le compte de résultat"
 subtitle="Les soldes se ventilent dans les états financiers selon la classe du compte."
 />

 <div className="mt-6 grid md:grid-cols-2 gap-6">
 {/* Bilan */}
 <motion.div
 initial={{ opacity: 0, x: -10 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.1 }}
 className="rounded-xl border border-keyto overflow-hidden"
 >
 <header className="px-4 py-3 bg-[#15294F] text-white flex items-center justify-between">
 <h3 className="font-heading font-bold">BILAN</h3>
 <span className="text-[10px] opacity-70">Classes 1 à 5</span>
 </header>
 <div className="grid grid-cols-2 divide-x divide-keyto">
 <Side title="ACTIF" lines={bilanActif} total={totalsByZone["bilan-actif"].d} side="D" />
 <Side title="PASSIF" lines={bilanPassif} total={totalsByZone["bilan-passif"].c} side="C" extra={resultat !== 0 ? { lib:"Résultat de l'exercice (12)", value: resultat } : null} />
 </div>
 </motion.div>

 {/* Compte de résultat */}
 <motion.div
 initial={{ opacity: 0, x: 10 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.25 }}
 className="rounded-xl border border-keyto overflow-hidden"
 >
 <header className="px-4 py-3 bg-[#B85724] text-white flex items-center justify-between">
 <h3 className="font-heading font-bold">COMPTE DE RÉSULTAT</h3>
 <span className="text-[10px] opacity-70">Classes 6 & 7</span>
 </header>
 <div className="grid grid-cols-2 divide-x divide-keyto">
 <Side title="CHARGES (6)" lines={crCharges} total={totalsByZone["cr-charge"].d} side="D" />
 <Side title="PRODUITS (7)" lines={crProduits} total={totalsByZone["cr-produit"].c} side="C" />
 </div>
 <div className="px-4 py-3 bg-slate-50 border-t border-keyto flex items-center justify-between">
 <span className="text-sm font-semibold text-[#15294F]">
 {resultat >= 0 ?"Bénéfice" :"Perte"} de l&apos;exercice
 </span>
 <span
 className="font-mono font-bold"
 style={{ color: resultat >= 0 ?"#2F7A5C" :"#A33B4B" }}
 >
 {fmt(Math.abs(resultat))} €
 </span>
 </div>
 </motion.div>
 </div>

 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.5 }}
 className="mt-6 p-4 rounded-xl bg-[#15294F] text-white text-sm leading-relaxed"
 >
 <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color:"#5AA0FF" }}>
 Le déclic
 </p>
 Le <strong>résultat</strong> calculé dans le compte de résultat (
 produits − charges = <strong>{fmt(resultat)} €</strong>) <strong>bascule au passif du bilan</strong> (compte 12).
 C&apos;est ainsi que le bilan s&apos;équilibre : Actif = Passif + Résultat.
 Tout est lié.
 </motion.div>
 </motion.div>
 );
}

function Side({ title, lines, total, side, extra }) {
 return (
 <div className="p-4">
 <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">{title}</p>
 <ul className="space-y-1 text-xs">
 {lines.length === 0 && <li className="text-slate-400 italic">—</li>}
 {lines.map((l) => {
 const c = COLOR_BY_CLASS[l.classe];
 const val = side ==="D" ? l.debit - l.credit : l.credit - l.debit;
 return (
 <li key={l.num} className="flex justify-between gap-3">
 <span>
 <span className="font-mono font-bold" style={{ color: c }}>{l.num}</span>{""}
 <span className="text-slate-600">{l.lib}</span>
 </span>
 <span className="font-mono text-[#15294F]">{fmt(val)}</span>
 </li>
 );
 })}
 {extra && (
 <li className="flex justify-between gap-3 text-[#2D6BE0] font-semibold pt-1.5 mt-1.5 border-t border-keyto">
 <span>{extra.lib}</span>
 <span className="font-mono">{fmt(extra.value)}</span>
 </li>
 )}
 </ul>
 <div className="mt-3 pt-2 border-t-2 border-slate-200 flex justify-between text-xs font-bold text-[#15294F]">
 <span>Total</span>
 <span className="font-mono">{fmt(total + (extra?.value || 0))}</span>
 </div>
 </div>
 );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
 return (
 <div className="flex items-start gap-3">
 <div className="shrink-0 w-10 h-10 rounded-lg bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <Icon size={18} className="text-[#2D6BE0]" />
 </div>
 <div>
 <h2 className="font-heading text-2xl font-bold text-[#15294F]">{title}</h2>
 <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
 </div>
 </div>
 );
}

const EXPLAIN = {
 0: {
 titre:"Étape 1 — Le journal",
 txt:"Toutes les opérations sont d'abord saisies au journal, dans l'ordre chronologique. Chaque opération est une « écriture » composée de plusieurs lignes, équilibrées en débit/crédit (partie double).",
 },
 1: {
 titre:"Étape 2 — Le grand livre",
 txt:"Le grand livre reprend ces mêmes lignes mais les regroupe par compte. Chaque compte devient un « T » avec tous ses débits et crédits. C'est la photo détaillée de chaque rubrique.",
 },
 2: {
 titre:"Étape 3 — La balance",
 txt:"La balance synthétise le grand livre : un compte = une ligne, avec ses totaux et son solde. Si Total Débits = Total Crédits, la partie double est respectée. C'est un outil de contrôle.",
 },
 3: {
 titre:"Étape 4 — Bilan & compte de résultat",
 txt:"Enfin, les soldes se ventilent : les comptes des classes 1 à 5 vont au bilan, ceux des classes 6 et 7 au compte de résultat. Le résultat calculé revient ensuite au passif du bilan — la boucle est bouclée.",
 },
};

function ExplainBox({ step }) {
 const info = EXPLAIN[step];
 return (
 <motion.div
 key={step}
 initial={{ opacity: 0, y: 6 }}
 animate={{ opacity: 1, y: 0 }}
 className="bg-white border border-keyto rounded-xl p-5 shadow-keyto-sm"
 data-testid="flux-explain"
 >
 <p className="text-[10px] font-semibold text-[#2D6BE0] uppercase tracking-wider mb-1.5">
 Explication pédagogique
 </p>
 <h3 className="font-heading font-bold text-[#15294F] mb-1">{info.titre}</h3>
 <p className="text-sm text-slate-600 leading-relaxed">{info.txt}</p>
 </motion.div>
 );
}
